<template>
  <div class="flex items-center mb-5">
    <div>
      <div class="text-xl">{{ t("users.title") }}</div>
      <div class="text-medium-emphasis text-sm">
        {{ t("users.description") }}
      </div>
    </div>
    <v-spacer></v-spacer>
    <v-btn append-icon="mdi-plus" border @click="showDialog = true">{{ t("users.add") }}</v-btn>
  </div>
  <v-table hover density="compact">
    <thead>
      <tr>
        <th class="text-left">{{ t("common.email") }}</th>
        <th class="text-left">{{ t("users.role") }}</th>
        <th class="text-left">{{ t("users.createdAt") }}</th>
        <th class="text-left">{{ t("users.lastSignInAt") }}</th>
        <th class="text-left"></th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="user in users" :key="user.uid">
        <td>
          {{ user.email }}
        </td>
        <td>
          <v-chip :color="user.role === 'externalApprover' ? 'default' : 'primary'" size="small">
            {{ t(`users.${user.role}`) }}
          </v-chip>
        </td>
        <td>{{ user.createdAt ? getRelativeTime(user.createdAt.toDate()) : "-" }}</td>
        <td>
          <span v-if="user.lastSignInAt">
            {{ getRelativeTime(user.lastSignInAt.toDate()) }}
          </span>
          <span v-else class="text-medium-emphasis text-sm">{{ t("users.invitationSent") }}</span>
        </td>
        <td class="text-right">
          <div v-if="currentUser.isKind('admin')" class="flex justify-end gap-2">
            <v-btn
              size="small"
              color="error"
              variant="outlined"
              :disabled="!!deletingUserId || resettingMfa"
              data-testid="reset-user-mfa-btn"
              @click="openResetMfa(user)"
              >{{ t("users.resetMfa") }} [ADMIN]</v-btn
            >
            <v-btn
              size="small"
              color="error"
              variant="outlined"
              :loading="deletingUserId === user.uid"
              :disabled="deletingUserId === user.uid || resettingMfa"
              @click="removeUser(user)"
              >{{ t("users.delete") }} [ADMIN]</v-btn
            >
          </div>
        </td>
      </tr>
    </tbody>
  </v-table>
  <v-dialog v-model="showDialog" width="600" persistent>
    <v-card>
      <v-form ref="addUserForm" :disabled="creatingUser" @submit.prevent="sendInvite">
        <v-card-title>{{ t("users.addUser") }}</v-card-title>
        <v-card-text>
          <v-text-field
            v-model.trim="email"
            :label="t('common.email')"
            :rules="[required, validEmail]"
          ></v-text-field>
          <v-radio-group
            v-model="role"
            color="primary"
            class="-ml-3"
            :label="t('users.role')"
            inline
          >
            <v-radio
              v-for="assignableRole in assignableRoles"
              :key="assignableRole"
              :label="t(`users.${assignableRole}`)"
              :value="assignableRole"
            ></v-radio>
          </v-radio-group>
          <v-alert border variant="tonal" density="compact" color="primary" type="info">
            {{ t("users.inviteEmailInfo") }}
          </v-alert>
          <v-alert
            v-if="error && !creatingUser"
            class="mt-3"
            border
            variant="tonal"
            density="compact"
            color="error"
            type="error"
            >{{ error }}</v-alert
          >
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn :disabled="creatingUser" @click="showDialog = false">{{ t("users.close") }}</v-btn>
          <v-btn
            :disabled="!!error || creatingUser"
            :loading="creatingUser"
            color="primary"
            variant="flat"
            type="submit"
          >
            {{ t("users.add") }}
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
  <v-dialog v-model="showResetMfaDialog" max-width="640" scrollable persistent>
    <v-card>
      <v-form :disabled="resettingMfa" @submit.prevent="confirmResetMfa">
        <v-card-title class="text-wrap">{{ t("users.resetMfaTitle") }}</v-card-title>
        <v-card-text>
          <div class="mb-3 font-bold">
            {{ t("users.resetMfaFor", { email: resetMfaUser?.email }) }}
          </div>
          <v-alert type="error" color="error" variant="tonal" border class="mb-4">
            <p>{{ t("users.resetMfaWarning") }}</p>
            <p class="mt-2 font-bold">{{ t("users.resetMfaDoubt") }}</p>
          </v-alert>
          <p class="mb-2">{{ t("users.resetMfaVerifyIntro") }}</p>
          <ul class="mb-4 flex list-disc flex-col gap-2 pl-5 text-sm">
            <li>{{ t("users.resetMfaVerifyVideo") }}</li>
            <li>{{ t("users.resetMfaVerifyPhone") }}</li>
            <li>{{ t("users.resetMfaVerifyEmail") }}</li>
            <li>{{ t("users.resetMfaVerifyPressure") }}</li>
          </ul>
          <v-checkbox
            v-model="resetMfaIdentityConfirmed"
            color="primary"
            hide-details
            :label="t('users.resetMfaConfirmIdentity')"
            data-testid="reset-user-mfa-identity"
          ></v-checkbox>
          <v-text-field
            v-model.trim="resetMfaEmailConfirmation"
            class="mt-3"
            autocomplete="off"
            :label="t('users.resetMfaTypeEmail')"
            data-testid="reset-user-mfa-email"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn type="button" :disabled="resettingMfa" @click="closeResetMfa">{{
            t("common.cancel")
          }}</v-btn>
          <v-btn
            type="submit"
            color="error"
            variant="flat"
            :loading="resettingMfa"
            :disabled="
              resettingMfa ||
              !resetMfaIdentityConfirmed ||
              resetMfaEmailConfirmation.toLowerCase() !== resetMfaUser?.email.toLowerCase()
            "
            data-testid="reset-user-mfa-confirm"
          >
            {{ t("users.resetMfaConfirm") }}
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { useCollection } from "@/composables/useFirestore";
import { useI18n } from "@/composables/useI18n";
import { useRelativeTime } from "@/composables/useRelativeTime";
import { useRules } from "@/composables/useRules";
import { call, db } from "@/firebase";
import { useToast } from "@/store/toast";
import { useUserStore } from "@/store/user";
import { transferRequestApproverRoles } from "@aircap/common/auth";
import type { SupervisorOrgUser, SupervisorOrgUserRole } from "@aircap/common/supervisorOrg";
import {
  supervisorOrgUserRoleSchema,
  supervisorOrgUsersFullPath,
} from "@aircap/common/supervisorOrg";
import { captureException } from "@sentry/vue";
import { collection, orderBy, query } from "firebase/firestore";
import { computed, ref, watch } from "vue";
import { VForm } from "vuetify/components";

const { t } = useI18n();
const currentUser = useUserStore();
const { getRelativeTime } = useRelativeTime();
const { required, validEmail } = useRules();
const toast = useToast();

const usersRef = computed(() =>
  currentUser.supervisorOrgId
    ? query(
        collection<SupervisorOrgUser>(db, supervisorOrgUsersFullPath(currentUser.supervisorOrgId)),
        orderBy<SupervisorOrgUser>("createdAt", "asc"),
      )
    : null,
);
const isNonApprovingSupervisor = computed(
  () =>
    !currentUser.isKind("admin") &&
    currentUser.hasRole(["supervisor.nonApprovingSupervisor"], "supervisorOrg"),
);

const assignableRoles = computed((): SupervisorOrgUserRole[] => {
  if (isNonApprovingSupervisor.value) {
    return supervisorOrgUserRoleSchema.options.filter(
      (role) => !transferRequestApproverRoles.includes(`supervisor.${role}`),
    );
  }
  return [...supervisorOrgUserRoleSchema.options];
});

const defaultAssignableRole = computed((): SupervisorOrgUserRole =>
  isNonApprovingSupervisor.value ? "nonApprovingSupervisor" : "supervisor",
);

const users = useCollection<SupervisorOrgUser>(usersRef);
const showDialog = ref(false);
const email = ref("");
const role = ref<SupervisorOrgUserRole>(defaultAssignableRole.value);
const deletingUserId = ref("");
const creatingUser = ref(false);
const showResetMfaDialog = ref(false);
const resetMfaUser = ref<SupervisorOrgUser | null>(null);
const resetMfaIdentityConfirmed = ref(false);
const resetMfaEmailConfirmation = ref("");
const resettingMfa = ref(false);
const addUserForm = ref<VForm | null>(null);
const error = computed(() => {
  if (users.value.find((user) => user.email === email.value)) {
    return t("users.alreadyInvited");
  }
  return "";
});

watch(showDialog, (open) => {
  if (open) role.value = defaultAssignableRole.value;
});

const sendInvite = async () => {
  if (error.value) return;
  if (!currentUser.supervisorOrgId) return;
  const validateForm = await addUserForm.value?.validate();
  if (!validateForm?.valid) return;

  try {
    console.log("Sending invite...");
    creatingUser.value = true;
    await call("createSupervisorOrgUser")({
      email: email.value.toLowerCase(),
      supervisorOrgId: currentUser.supervisorOrgId,
      role: role.value,
    });

    showDialog.value = false;
    email.value = "";
    role.value = defaultAssignableRole.value;
  } catch (cause) {
    const error = new Error("Error creating user", { cause });
    console.error(error);
    captureException(error);
    toast.showErrorToast();
  } finally {
    creatingUser.value = false;
  }
};

const openResetMfa = (user: SupervisorOrgUser) => {
  resetMfaUser.value = user;
  resetMfaIdentityConfirmed.value = false;
  resetMfaEmailConfirmation.value = "";
  showResetMfaDialog.value = true;
};

const closeResetMfa = () => {
  if (resettingMfa.value) return;
  showResetMfaDialog.value = false;
};

const confirmResetMfa = async () => {
  if (!currentUser.supervisorOrgId || !resetMfaUser.value || !resetMfaIdentityConfirmed.value)
    return;
  if (resetMfaEmailConfirmation.value.toLowerCase() !== resetMfaUser.value.email.toLowerCase())
    return;

  try {
    console.log("Resetting user MFA...");
    resettingMfa.value = true;
    const { refreshTokensRevoked } = await call("resetUserMfa")({
      supervisorOrgId: currentUser.supervisorOrgId,
      userId: resetMfaUser.value.uid,
      email: resetMfaEmailConfirmation.value,
    });
    showResetMfaDialog.value = false;
    resetMfaIdentityConfirmed.value = false;
    resetMfaEmailConfirmation.value = "";
    if (refreshTokensRevoked) toast.showSuccessToast(t("users.resetMfaSuccess"));
    else toast.showWarningToast(t("users.resetMfaSessionsRemain"));
  } catch (cause) {
    const error = new Error(`Error resetting MFA for user ${resetMfaUser.value?.uid}`, {
      cause,
    });
    console.error(error);
    captureException(error);
    toast.showErrorToast();
  } finally {
    resettingMfa.value = false;
  }
};

const removeUser = async (user: SupervisorOrgUser) => {
  if (!currentUser.supervisorOrgId) return;

  const confirmed = window.confirm(t("users.confirmDelete", { email: user.email }));
  if (!confirmed) {
    return;
  }

  try {
    console.log("Removing user...");
    deletingUserId.value = user.uid;
    await call("deleteSupervisorOrgUser")({
      userId: user.uid,
      supervisorOrgId: currentUser.supervisorOrgId,
    });
    deletingUserId.value = "";
    toast.showSuccessToast(t("users.deleteSuccess"));
  } catch (cause) {
    const error = new Error("Error deleting user", { cause });
    console.error(error);
    captureException(error);
    toast.showErrorToast(t("users.deleteError"));
  }
};
</script>
